import { useEffect, useState } from 'react'

import { fetchSchedules } from '../api/schedules'
import { fetchTodos, TODOS_UPDATED_EVENT } from '../api/todos'

const IS_TEST = import.meta.env.MODE === 'test'

export interface UnseenCounts {
  todos: number
  schedules: number
}

interface UseUnseenCountsOptions {
  enabled?: boolean
  intervalMs?: number
}

export function useUnseenCounts(
  refreshKey: unknown = null,
  { enabled = !IS_TEST, intervalMs = 10_000 }: UseUnseenCountsOptions = {},
): UnseenCounts {
  const [counts, setCounts] = useState<UnseenCounts>({ todos: 0, schedules: 0 })

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    let controller: AbortController | null = null

    function refresh() {
      controller?.abort()
      controller = new AbortController()
      const activeController = controller
      Promise.allSettled([
        fetchTodos(activeController.signal),
        fetchSchedules(activeController.signal, false),
      ])
        .then(([todosResult, schedulesResult]) => {
          if (cancelled || activeController.signal.aborted) return
          const todos = todosResult.status === 'fulfilled'
            ? todosResult.value.active.filter((item) => !item.seen_at).length
            : 0
          const schedules = schedulesResult.status === 'fulfilled'
            ? schedulesResult.value.jobs.filter((item) => !item.seen_at).length
            : 0
          setCounts({ todos, schedules })
        })
        .catch(() => {
          if (!cancelled && !activeController.signal.aborted) setCounts({ todos: 0, schedules: 0 })
        })
    }

    refresh()
    const intervalId = intervalMs > 0 ? window.setInterval(refresh, intervalMs) : null
    window.addEventListener(TODOS_UPDATED_EVENT, refresh)
    window.addEventListener('credem:schedules-updated', refresh)
    window.addEventListener('focus', refresh)
    return () => {
      cancelled = true
      controller?.abort()
      if (intervalId !== null) window.clearInterval(intervalId)
      window.removeEventListener(TODOS_UPDATED_EVENT, refresh)
      window.removeEventListener('credem:schedules-updated', refresh)
      window.removeEventListener('focus', refresh)
    }
  }, [enabled, intervalMs, refreshKey])

  return counts
}
